'use client';
import Image from '@/components/Image';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import Tooltip from '@/components/Tooltip';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { MinusOutlined, QuestionFilled, QuestionOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import React, { useContext, useEffect, useState } from 'react';

export default function ExclusiveRewards({
  memberPrivileges,
  memberLevelConfig,
  userInfo,
  showTitle = true,
}: {
  memberPrivileges: any;
  memberLevelConfig?: any;
  userInfo?: any;
  showTitle?: boolean;
}) {
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const token = useStore((state) => state.token);
  const t = useTranslations('zeeloolRewards.exclusiveRewards');
  const [rewardsData, setRewardsData] = useState({
    tiers: [
      {
        name: memberPrivileges.level_info[0].name,
        spend: t('spendAmount', {
          amount: memberPrivileges.level_info[0].score,
        }),
        color: memberLevelConfig.v0.bgcolor,
      },
      {
        name: memberPrivileges.level_info[1].name,
        spend: t('spendAmount', {
          amount: memberPrivileges.level_info[1].score,
        }),
        color: memberLevelConfig.v1.bgcolor,
      },
      {
        name: memberPrivileges.level_info[2].name,
        spend: t('spendAmount', {
          amount: memberPrivileges.level_info[2].score,
        }),
        color: memberLevelConfig.v2.bgcolor,
      },
      {
        name: memberPrivileges.level_info[3].name,
        spend: t('spendAmount', {
          amount: memberPrivileges.level_info[3].score,
        }),
        color: memberLevelConfig.v3.bgcolor,
      },
    ],
    rewards: [
      // {
      //   name: 'BOGO 50% OFF',
      //   values: [
      //     '50% OFF Complete Pair up to 2 pairs',
      //     '50% OFF Complete Pair up to 3 pairs',
      //     '50% OFF Complete Pair up to 4 pairs',
      //     '50% OFF Complete Pair up to 6 pairs',
      //   ],
      // },
      // {
    ],
  });

  function buildValues(id: any) {
    const values = [];
    for (let i = 0; i < memberPrivileges.member_level_privileges.length; i++) {
      const level_privilege = memberPrivileges.member_level_privileges[i];
      if (level_privilege[id]) {
        values.push(level_privilege[id]);
      } else {
        values.push({
          type: 'text',
          value: '-',
        });
      }
    }
    return values;
  }

  const privilegesItemIsShow = (id: string) => {
    const privileges0 = memberPrivileges.member_level_privileges[0][id];
    const privileges1 = memberPrivileges.member_level_privileges[1][id];
    const privileges2 = memberPrivileges.member_level_privileges[2][id];
    const privileges3 = memberPrivileges.member_level_privileges[3][id];
    return privileges0 || privileges1 || privileges2 || privileges3;
  };

  function buildRewards() {
    const rewards = [];
    for (const equity_statement_item of memberPrivileges.equity_statement) {
      if (privilegesItemIsShow(equity_statement_item.id)) {
        const item = {
          id: equity_statement_item.id,
          name: equity_statement_item.name,
          values: [],
          description: equity_statement_item.description,
        };

        const values = buildValues(equity_statement_item.id);

        item.values = values;

        rewards.push(item);
      }
    }
    return rewards;
  }

  useEffect(() => {
    const rewards = buildRewards();
    setRewardsData({ ...rewardsData, rewards });
  }, [memberPrivileges]);

  function renderValue(value: any) {
    if (value.type === 'text') {
      if (value.value === '-') {
        return (
          <div
            className={`flex items-center justify-center rounded-full bg-[#A9A9A9] relative w-[20px] h-[20px]`}
          >
            <MinusOutlined className={`font-br relative text-white text-14`} />
          </div>
        );
      }
      return (
        <span className="text-14 font-br relative">
          {value.value}{' '}
          {value.description && (
            <Tooltip
              title={value.description}
              trigger={isWeb ? 'hover' : 'click'}
              placement="right"
              // 防止WAP端的提示溢出导致页面变宽超过屏幕
              maxWidth={isWeb ? 280 : 170}
              sx={{ zIndex: 3 }}
            >
              {isWeb ? (
                <QuestionFilled className="ml-[4px] -translate-y-[1px] inline-block cursor-pointer opacity-40" />
              ) : (
                <QuestionOutlined className="ml-[4px] translate-y-[2px] inline-block cursor-pointer" />
              )}
            </Tooltip>
          )}
        </span>
      );
    }
    if (value.type === 'image') {
      return (
        <Image
          src={value.value}
          alt={value.alt || 'value'}
          width={73}
          height={35}
          className="w-[73px] h-[35px] relative"
        />
      );
    }
    if (value.type === 'switch') {
      return (
        <Image
          src={'/account/zeelool-rewards/check.png'}
          alt="check"
          width={20}
          height={20}
          className="mx-auto relative"
        />
      );
    }
    return <span className="text-xs">{value.value}</span>;
  }

  function getTierColor(index: number) {
    try {
      return rewardsData.tiers[index].color;
    } catch (error) {
      logger.log(error, '@@error');
      return '';
    }
  }
  function getLevelColor(index: number) {
    try {
      return userInfo?.member_level === index || !userInfo || userInfo?.member_level < 0 || !token
        ? `${rewardsData.tiers[index].color} rounded-12`
        : 'bg-[#f4f4f4]';
    } catch (error) {
      logger.log(error, '@@error');
      return '';
    }
  }
  return (
    <>
      <WebOnly>
        <section
          className={`w-full bg-white box-border border border-[#E0E0E0] rounded-12 overflow-hidden`}
        >
          <div className="container mx-auto">
            <div className="overflow-x-auto overflow-y-hidden">
              <div className={`grid grid-cols-5 min-w-[1185px]`}>
                <div className="sticky text-16 left-0 z-9 flex items-center justify-start px-12 h-[64px] min-w-[237px] box-border font-bb bg-[#f4f4f4] rounded-tl-12 text-[#808080] shadow-[10px_10px_10px_rgba(0,0,0,0.1)]">
                  {t('tableHeader')}
                </div>
                {rewardsData.tiers.map((tier, index) => (
                  <div key={tier.name} className="relative min-w-[237px] box-border h-[64px]">
                    {userInfo?.member_level === index && (
                      <div
                        className={`${tier.color} opacity-30 absolute top-0 left-0 w-full h-full `}
                      />
                    )}
                    <div
                      className={`text-center text-16 h-[64px] flex items-center justify-center flex-col relative z-7 ${getLevelColor(index)}`}
                    >
                      <h3 className="font-bb">{tier.name}</h3>
                      <p className="text-14 font-normal">{tier.spend}</p>
                    </div>
                  </div>
                ))}

                {rewardsData.rewards.map((reward, rewardIndex) => (
                  <React.Fragment key={reward.name}>
                    <div
                      className={`sticky left-0 z-9 flex items-center pl-12 h-[64px] min-w-[237px] box-border shadow-[10px_10px_10px_rgba(0,0,0,0.1)] ${
                        rewardIndex % 2 === 0 ? 'bg-white' : 'bg-[#f7f7f7]'
                      } ${rewardIndex === rewardsData.rewards.length - 1 ? 'rounded-bl-12' : ''}`}
                    >
                      <span className="font-bb text-[16px] leading-[20px]">
                        {reward.name}
                        {reward.description && (
                          <Tooltip title={reward.description} trigger="hover" placement="right">
                            <QuestionFilled className="ml-[4px] -translate-y-[2px] inline-block cursor-pointer opacity-40" />
                          </Tooltip>
                        )}
                      </span>
                    </div>
                    {reward.values.map((value, valueIndex) => {
                      const tierColor = getTierColor(valueIndex);
                      return (
                        <div key={valueIndex} className="relative min-w-[237px]">
                          {userInfo?.member_level === valueIndex && (
                            <div
                              className={`${tierColor} opacity-30 absolute top-0 left-0 w-full h-full`}
                            />
                          )}
                          <div
                            key={valueIndex}
                            className={`px-[34.5px] h-[64px] flex justify-center items-center text-center text-[14px] leading-[16px] ${
                              rewardIndex % 2 === 0 ? '' : 'bg-[#f7f7f7]'
                            }`}
                          >
                            {renderValue(value)}
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>
      </WebOnly>
      <WapOnly>
        <>
          {showTitle && (
            <div className="text-[24px] font-bb mt-[52px] text-center">{t('title')}</div>
          )}
          <section
            className={`w-[calc(100%-14px)] ml-14 bg-white box-border border border-[#E0E0E0] rounded-tl-12 rounded-bl-12 overflow-hidden ${showTitle ? 'mt-20' : 'mt-28'}`}
          >
            <div className="container mx-auto">
              <div className="overflow-x-auto overflow-y-hidden">
                <div className="grid grid-cols-5 w-[775px]">
                  <div className="sticky left-0 z-8 text-14 flex items-center justify-start px-12 h-[55px] !w-[155px] box-border font-bb bg-[#f4f4f4] rounded-tl-12 text-[#808080] shadow-[5px_5px_5px_rgba(0,0,0,0.1)]">
                    {t('tableHeader')}
                  </div>
                  {rewardsData.tiers.map((tier, index) => (
                    <div key={tier.name} className="relative !w-[155px] box-border h-[55px]">
                      {userInfo?.member_level === index && (
                        <div
                          className={`${tier.color} opacity-30 absolute top-0 left-0 w-full h-full`}
                        />
                      )}
                      <div
                        className={`text-center h-[55px] flex items-center justify-center flex-col relative z-7 ${getLevelColor(index)}`}
                      >
                        <h3 className="text-14 font-bb">{tier.name}</h3>
                        <p className="text-12 font-br mt-4">{tier.spend}</p>
                      </div>
                    </div>
                  ))}

                  {rewardsData.rewards.map((reward, rewardIndex) => (
                    <React.Fragment key={reward.name}>
                      <div
                        className={`sticky left-0 z-9 flex items-center pl-12 h-[55px] !w-[155px] box-border ${
                          rewardIndex % 2 === 0 ? 'bg-white' : 'bg-[#f7f7f7]'
                        } shadow-[5px_5px_5px_rgba(0,0,0,0.1)] ${
                          rewardIndex === rewardsData.rewards.length - 1 ? 'rounded-bl-12' : ''
                        }`}
                      >
                        <span className="font-bb text-[13px] leading-[16px]">
                          {reward.name}
                          {reward.description && (
                            <Tooltip
                              title={reward.description}
                              trigger="click"
                              placement="right"
                              maxWidth={isWeb ? 280 : 200}
                            >
                              <QuestionOutlined className="ml-[4px] translate-y-[2px]" />
                            </Tooltip>
                          )}
                        </span>
                      </div>
                      {reward.values.map((value, valueIndex) => {
                        const tierColor = getTierColor(valueIndex);
                        return (
                          <div key={valueIndex} className="relative !w-[155px]">
                            {userInfo?.member_level === valueIndex && (
                              <div
                                className={`${tierColor} opacity-30 absolute top-0 left-0 w-full h-full `}
                              />
                            )}
                            <div
                              key={valueIndex}
                              className={`px-12 h-[55px] flex justify-center items-center text-center text-[13px] leading-[16px] ${
                                rewardIndex % 2 === 0 ? '' : 'bg-[#f7f7f7]'
                              }`}
                            >
                              {renderValue(value)}
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </>
      </WapOnly>
    </>
  );
}
